import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { PropertyDetailsFragment } from '@client/graphql/__generated__/types';
import { Form, Formik } from 'formik';
import { FC } from 'react';
import {
  EditOffMarketListingFormData,
  FIELD_TO_LABEL_MAP,
  OffMarketListingFormData,
  REQUIRED_KEY_TERM_FIELDS,
} from '../AddOffMarketListing/constants';
import { PropertyDetailsStep } from '../AddOffMarketListing/PropertyDetailsStep';
import { useEditOffMarketListingActions } from './useEditOffMarketListingActions';

// Fields that will be edited in this modal (non-address fields)
export const EDITABLE_FIELDS = [
  'targetPrice',
  'squareFootage',
  'yearBuilt',
  'bedrooms',
  'fullBaths',
  'halfBaths',
  'propertyType',
  'propertySubtype',
  'subdivision',
  'schoolDistrict',
];

interface EditOffMarketListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyDetails: PropertyDetailsFragment;
  onSave: () => void;
}

export const EditOffMarketListingModal: FC<EditOffMarketListingModalProps> = ({
  isOpen,
  onClose,
  propertyDetails,
  onSave,
}) => {
  const {
    isOpen: isTooltipOpen,
    onOpen: onTooltipOpen,
    onClose: onTooltipClose,
  } = useDisclosure();

  const { handleSubmit, isLoading } = useEditOffMarketListingActions({
    onClose,
    onSave,
  });

  // Only include editable fields in initialValues
  const initialValues = {
    targetPrice: propertyDetails?.targetPrice || undefined,
    squareFootage: propertyDetails?.areaSqFt || undefined,
    yearBuilt: propertyDetails?.yearBuilt || undefined,
    bedrooms: propertyDetails?.bedrooms || undefined,
    fullBaths: propertyDetails?.bathsFull || undefined,
    halfBaths: propertyDetails?.bathsHalf || undefined,
    propertyType: propertyDetails?.propertyType || '',
    propertySubtype: propertyDetails?.propertySubtype || '',
    subdivision: propertyDetails?.subdivision || '',
    schoolDistrict: propertyDetails?.schoolDistrict || '',
  };

  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <Formik
        initialValues={initialValues}
        onSubmit={(values: EditOffMarketListingFormData) =>
          handleSubmit(propertyDetails?.id || '', values)
        }
      >
        {(props) => {
          const values = props.values;

          const isButtonDisabled = !REQUIRED_KEY_TERM_FIELDS.every(
            (field) => values[field as keyof typeof values]
          );

          const getTooltipLabel = () => (
            <Flex direction="column">
              {REQUIRED_KEY_TERM_FIELDS.map((field) => {
                if (!values[field as keyof typeof values]) {
                  return (
                    <Text key={field}>
                      {
                        FIELD_TO_LABEL_MAP[
                          field as keyof OffMarketListingFormData
                        ]
                      }{' '}
                      required
                    </Text>
                  );
                }

                return null;
              })}
            </Flex>
          );

          return (
            <Form>
              <ModalContent maxHeight="80vh">
                <ModalHeader>Edit Off Market Listing</ModalHeader>

                <ModalCloseButton />
                <ModalBody overflow="auto" p={5}>
                  <VStack align="stretch" spacing={5}>
                    <PropertyDetailsStep />
                  </VStack>
                </ModalBody>
                <ModalFooter bottom={0} p={3} position="sticky">
                  <Button size="sm" variant="ghost" onClick={onClose}>
                    Cancel
                  </Button>

                  <Tooltip
                    isOpen={isTooltipOpen}
                    label={getTooltipLabel()}
                    placement="top"
                    onClose={onTooltipClose}
                  >
                    <span
                      tabIndex={0}
                      onClick={() => {
                        if (isButtonDisabled) {
                          onTooltipOpen();
                        }
                      }}
                      onMouseEnter={() => {
                        if (isButtonDisabled) {
                          onTooltipOpen();
                        }
                      }}
                      onMouseLeave={() => {
                        if (isTooltipOpen) {
                          onTooltipClose();
                        }
                      }}
                    >
                      <Button
                        isDisabled={isButtonDisabled || !props.dirty}
                        isLoading={isLoading}
                        size="sm"
                        type="submit"
                      >
                        Save Changes
                      </Button>
                    </span>
                  </Tooltip>
                </ModalFooter>
              </ModalContent>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
